<template>
  <div>
    <ConfirmExit
      v-if="confirmExit.isLeavingRoute"
      v-on:isLeavingRoute="$emit('proceedLeave')"
      v-on:isNotLeavingRoute="$emit('cancelLeave')"
      :text="isCreateRole ? i18n.areYouSureYouWantToLeaveAdd :i18n.areYouSureYouWantToLeaveEdit"
    />
    <RoleHeader
      :title="i18n.title"
      :svgSource="getSvgIconSrc('Roles')"
      :buttons="buttons"
    />
    <v-row class="mt-6">
      <v-col cols="8">
        <RoleList
          v-if="this.isListRoles || this.isViewPermissions"
          :roles="roles"
          @deleteRole="showDeleteConfirmation"
          @viewRole="showRolePermissions"
          @editRole="showPermissionsForEditing"
          @refreshRoles="refreshRoles"
        />
        <RoleInput
          v-if="isCreateRole || isEditRole"
          v-model="newCorporateRole"
          :isCreateMode="isCreateRole"
          :isFormValid.sync="isRoleInputValid"
          :isDirty.sync="isDirty.roleInput"
        />
      </v-col>
      <v-col cols="4">
        <v-card v-if="isListRoles">
          <v-card-title
            class="role-card-title"
            v-text="i18n.rolePermissions"
          />
          <v-card-subtitle
            v-text="i18n.selectARoleToDisplay"
          />
        </v-card>
        <RolePermissionEdit
          v-if="isCreateRole || isEditRole"
          v-model=permissions
          :isDirty.sync="isDirty.rolePermissions"
        />
        <RolePermissionList
          v-if="isViewPermissions"
          :corporateCode="selectedRole.defaultRoleFlag === yes ? globalCorporate : corporateCode"
          :roleCode="selectedRole.roleCode"
        />
      </v-col>
    </v-row>

    <Loader v-show="loaderEnabled"/>
    <Modal :config="modalConfig" />

    <OtpHandler
      v-if="shouldShowOtpScreen"
      @otp-cancelled="shouldShowOtpScreen = false"
      @otp-success="handleOtpSuccess"
    />
  </div>
</template>

<script>
import '@/styles/pages/roles/roles.scss'
import RoleHeader from './RoleHeader.vue'
import RoleList from './RoleList.vue'
import RolePermissionEdit from './RolePermissionEdit.vue'
import RoleInput from './RoleInput.vue'
import Loader from '../../components/common/Loader.vue'
import store from '@/core/store'
import RolePermissionList from './RolePermissionList.vue'
import commonService from '../../service/commonService'

const ModalType = {
  ADDED: 'ADDED',
  CHANGED: 'CHANGED',
  DELETED: 'DELETED',
  DELETE_CONFIRM: 'DELETE_CONFIRM',
  DISCARD_CHANGES: 'DISCARD_CHANGES'
}

export default {
  components: {
    RoleHeader,
    RoleList,
    RolePermissionEdit,
    RolePermissionList,
    RoleInput,
    Loader,
    ConfirmExit: () => import('@/components/common/ConfirmExit.vue'),
    Modal: () => import('@/components/common/Modal.vue'),
    OtpHandler: () => import('@/components/common/OtpHandler.vue')
  },
  data () {
    return {
      currentMode: '',
      newCorporateRole: null,
      permissions: [],
      roles: [],
      loaderEnabled: false,
      selectedRole: null,
      confirmExit: {
        isLeavingRoute: false
      },
      modalConfig: {},
      isRoleInputValid: false,
      isDirty: {
        roleInput: false,
        rolePermissions: false
      },
      shouldShowOtpScreen: false,
      isAddRolesPermitted: false,
      isAssignPermissionAllowed: false,
      enableRoleBasedAccess: this.$appSettings.defaultConfigurations.smeRoles.enableRoleBasedAccess
    }
  },
  async created () {
    this.currentMode = this.mode.LIST_ROLES
    this.loaderEnabled = true
    await this.$nextTick()
    this.roles = await this.fetchDehRoleDetails()
    this.loaderEnabled = false
    // Fetch Permissions for SME Users
    this.permissions = store.getters['profileStore/profileData']?.permissions
    this.isAddRolesPermitted = this.enableRoleBasedAccess ? this.permissions?.includes('ADD_EDIT_AND_DELETE_ROLES') : true
    this.isAssignPermissionAllowed = this.enableRoleBasedAccess ? this.permissions?.includes('ASSIGN_PERMISSIONS') : true
  },
  beforeRouteLeave (to, from, next) {
    if (!(this.isCreateRole || this.isEditRole)) {
      return next()
    }
    this.confirmExit.isLeavingRoute = true
    this.$once('proceedLeave', () => {
      next()
    })
    this.$once('cancelLeave', () => {
      this.confirmExit.isLeavingRoute = false
      next(false)
    })
  },
  methods: {
    resetFormFields () {
      this.currentMode = this.mode.LIST_ROLES
      this.newCorporateRole = null
      this.permissions = []
      this.selectedRole = null
      this.isDirty.roleInput = false
      this.isDirty.rolePermissions = false
      this.shouldShowOtpScreen = false
    },
    async showLoader () {
      this.loaderEnabled = true
      await this.$nextTick()
    },
    viewRoleMaintenance () {
      this.resetFormFields()
    },
    async fetchDehRoleDetails () {
      try {
        return (await commonService.listDehRolesForSme(this.corporateCode)).dehRoleDetails || []
      } catch (error) {
        console.error('Error fetching DEH role details', error)
        return []
      }
    },
    async getPermissions () {
      try {
        return (await commonService.getPermissions()) || []
      } catch (error) {
        console.error('Error fetching DEH permissions', error)
        return []
      }
    },
    async getSmeRolePermissions () {
      try {
        const corporateCode = this.selectedRole.defaultRoleFlag === this.yes ? this.globalCorporate : this.corporateCode
        return (await commonService.getSmeRole(corporateCode, this.selectedRole.roleCode))?.permDetail ?? []
      } catch (error) {
        console.error('Error fetching SME role permissions', error)
        return []
      }
    },
    async deleteSmeRole () {
      try {
        const res = await commonService.deleteSmeRole(this.corporateCode, this.selectedRole.roleCode)
        if (res?.causes?.[0]) {
          return { error: true }
        }
        return { error: false }
      } catch (error) {
        console.error('Error deleting SME role', error)
        return { error: true }
      }
    },
    async addSmeRole (payload) {
      try {
        const res = await commonService.addSmeRole(payload)
        if (res?.causes?.[0]) {
          return { error: true }
        }
        return { error: false }
      } catch (error) {
        console.error('Error adding SME role', error)
        return { error: true }
      }
    },
    async updateSmeRole (payload) {
      try {
        const res = await commonService.updateSmeRole(payload)
        if (res?.causes?.[0]) {
          return { error: true }
        }
        return { error: false }
      } catch (error) {
        console.error('Error updating SME role', error)
        return { error: true }
      }
    },
    onSave () {
      if (this.appConfig.enableOtp) {
        this.shouldShowOtpScreen = true
        return
      }
      this.saveRole()
    },
    async saveRole () {
      await this.showLoader()
      const payload = {
        roleCode: this.newCorporateRole.roleName,
        roleName: this.newCorporateRole.roleName,
        roleDescription: this.newCorporateRole.roleDescription,
        corporateCode: this.corporateCode,
        permDetail: this.permDetail,
        createdByCustomerId: JSON.parse(sessionStorage.getItem('customerCode'))
      }
      const res = this.isCreateRole ? await this.addSmeRole(payload) : await this.updateSmeRole(payload)
      if (res.error) {
        this.loaderEnabled = false
        return
      }
      this.roles = await this.fetchDehRoleDetails()
      this.showModal(this.isCreateRole ? ModalType.ADDED : ModalType.CHANGED)
      this.loaderEnabled = false
      this.shouldShowOtpScreen = false
    },
    onCancel () {
      if (this.isDirty.roleInput || this.isDirty.rolePermissions) {
        this.showModal(ModalType.DISCARD_CHANGES)
        return
      }
      this.resetFormFields()
    },
    async showRoleInputForm () {
      await this.showLoader()
      this.permissions = await this.getPermissions()
      this.currentMode = this.mode.CREATE_ROLE
      this.loaderEnabled = false
    },
    async showPermissionsForEditing (item) {
      await this.showLoader()
      this.selectedRole = item
      this.newCorporateRole = {
        roleName: this.selectedRole.roleCode,
        roleDescription: this.selectedRole.roleDesc
      }
      this.permissions = await this.getSmeRolePermissions()
      this.permissions = this.permissions.map(p => ({
        ...p,
        enabled: p.assignedFlag === this.yes
      }))
      this.currentMode = this.mode.EDIT_ROLE
      this.loaderEnabled = false
    },
    async showRolePermissions (item) {
      await this.showLoader()
      this.selectedRole = item
      this.permissions = await this.getSmeRolePermissions()
      this.currentMode = this.mode.VIEW_PERMISSIONS
      this.loaderEnabled = false
    },
    cancelDelete () {
      this.resetFormFields()
    },
    onDelete () {
      if (this.appConfig.enableOtp) {
        this.shouldShowOtpScreen = true
        return
      }
      this.deleteRole()
    },
    async deleteRole () {
      await this.showLoader()
      const res = await this.deleteSmeRole()
      if (res.error) {
        this.loaderEnabled = false
        return
      }
      this.roles = await this.fetchDehRoleDetails()
      this.showModal(ModalType.DELETED)
      this.loaderEnabled = false
    },
    showDeleteConfirmation (item) {
      this.selectedRole = item
      this.showModal(ModalType.DELETE_CONFIRM)
    },
    async refreshRoles () {
      this.roles = await this.fetchDehRoleDetails()
    },
    showModal (modalType) {
      this.modalConfig = {
        ...this.modalConfigs[modalType],
        enabled: true,
        width: '400px'
      }
    },
    handleOtpSuccess () {
      this.shouldShowOtpScreen = false
      this.newCorporateRole ? this.saveRole() : this.deleteRole()
    }
  },

  computed: {
    corporateCode: () => JSON.parse(sessionStorage.getItem('corporateId')),
    globalCorporate: () => 'GLOBAL',
    yes: () => 'Y',
    no: () => 'N',
    mode () {
      return {
        LIST_ROLES: 0,
        CREATE_ROLE: 1,
        VIEW_PERMISSIONS: 2,
        EDIT_ROLE: 3
      }
    },
    appConfig () {
      return {
        enableOtp: this.$appSettings.defaultConfigurations.smeRoles.enableOtpForRoleMaintenance
      }
    },
    isListRoles () {
      return this.currentMode === this.mode.LIST_ROLES
    },
    isCreateRole () {
      return this.currentMode === this.mode.CREATE_ROLE
    },
    isViewPermissions () {
      return this.currentMode === this.mode.VIEW_PERMISSIONS
    },
    isEditRole () {
      return this.currentMode === this.mode.EDIT_ROLE
    },
    isValidInput () {
      return this.isRoleInputValid && this.enabledPermissions.length
    },
    permDetail () {
      return this.permissions
        .map(i => ({
          assignedFlag: i.enabled ? this.yes : this.no,
          permissionCode: i.permissionCode
        }))
    },
    enabledPermissions () {
      return this.permissions
        .filter(i => i.enabled)
    },
    i18n () {
      const t = key => this.$t(`roleMaintenance.${key}`)
      return {
        title: t('title'),
        rolePermissions: t('rolePermissions'),
        addedTitle: t('addedTitle'),
        changedTitle: t('changedTitle'),
        deletedTitle: t('deletedTitle'),
        addedText: t('addedText'),
        changedText: t('changedText'),
        deletedText: t('deletedText'),
        deleteConfirmTitle: t('deleteConfirmTitle'),
        deleteConfirmText: t('deleteConfirmText'),
        buttonAdd: t('buttonAdd'),
        buttonCancel: t('buttonCancel'),
        buttonSave: t('buttonSave'),
        buttonViewRoleMaintenance: t('buttonViewRoleMaintenance'),
        buttonContinueDelete: t('buttonContinueDelete'),
        buttonCancelDelete: t('buttonCancelDelete'),
        areYouSureYouWantToLeaveAdd: t('areYouSureYouWantToLeaveAdd'),
        areYouSureYouWantToLeaveEdit: t('areYouSureYouWantToLeaveEdit'),
        selectARoleToDisplay: t('selectARoleToDisplay'),
        discardUnsavedChanges: this.$t('common.discardUnsavedChanges'),
        confirmDiscardMessage: this.$t('common.confirmDiscardMessage'),
        button: {
          yes: this.$t('common.yes'),
          no: this.$t('common.no')
        }
      }
    },
    modalConfigs () {
      return {
        [ModalType.ADDED]: {
          title: this.i18n.addedTitle,
          subtitle: this.i18n.addedText,
          btn1Label: this.i18n.buttonViewRoleMaintenance,
          btn1Callback: this.viewRoleMaintenance
        },
        [ModalType.CHANGED]: {
          title: this.i18n.changedTitle,
          subtitle: this.i18n.changedText,
          btn1Label: this.i18n.buttonViewRoleMaintenance,
          btn1Callback: this.viewRoleMaintenance
        },
        [ModalType.DELETED]: {
          title: this.i18n.deletedTitle,
          subtitle: this.i18n.deletedText,
          btn1Label: this.i18n.buttonViewRoleMaintenance,
          btn1Callback: this.viewRoleMaintenance
        },
        [ModalType.DELETE_CONFIRM]: {
          title: this.i18n.deleteConfirmTitle,
          subtitle: this.i18n.deleteConfirmText,
          btn1Label: this.i18n.buttonContinueDelete,
          btn1Color: this.errorColor,
          btn1Callback: this.onDelete,
          btn2Label: this.i18n.buttonCancelDelete,
          btn2Color: this.errorColor,
          btn2Callback: this.cancelDelete
        },
        [ModalType.DISCARD_CHANGES]: {
          title: this.i18n.discardUnsavedChanges,
          subtitle: this.i18n.confirmDiscardMessage,
          btn1Label: this.i18n.button.yes,
          btn1Callback: this.resetFormFields,
          btn2Label: this.i18n.button.no
        }
      }
    },
    buttons () {
      return [
        {
          show: (this.isListRoles || this.isViewPermissions) && this.isAddRolesPermitted,
          label: this.i18n.buttonAdd,
          action: this.showRoleInputForm
        },
        {
          show: (this.isCreateRole || this.isEditRole) && this.isAssignPermissionAllowed,
          label: this.i18n.buttonCancel,
          action: this.onCancel
        },
        {
          show: (this.isCreateRole || this.isEditRole) && this.isAssignPermissionAllowed,
          label: this.i18n.buttonSave,
          action: this.onSave,
          disabled: !this.isValidInput || (!this.isDirty.roleInput && !this.isDirty.rolePermissions)
        }
      ]
    }
  }
}
</script>
